import { useState, useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

const API_BASE = "https://api.fitneks.com";

// ============================================
// MOCK DATA (replace with real API calls)
// ============================================
const MOCK_OVERVIEW = {
  totalUsers: 247,
  totalLearners: 203,
  totalTrainers: 44,
  signupsThisWeek: 34,
  signupsLastWeek: 22,
  signupTrend: 55,
  activeLearners7d: 89,
  activeLearners30d: 145,
  activeTrainers7d: 18,
  activeTrainers30d: 31,
  learnerRetention30d: 71,
  trainerRetention30d: 70,
  totalStreamsCompleted: 312,
};

const MOCK_LEARNER_FUNNEL = {
  totalSignups: 203,
  funnel: [
    { step: "Signed Up", count: 203, rate: 100, avgTimeFromPrevious: null },
    { step: "Completed Onboarding", count: 178, rate: 88, avgTimeFromPrevious: null },
    { step: "Attended First Class", count: 112, rate: 55, avgTimeFromPrevious: "18.4h" },
    { step: "Attended Second Class", count: 74, rate: 36, avgTimeFromPrevious: "52.1h" },
    { step: "Monthly Active", count: 61, rate: 30, avgTimeFromPrevious: null },
  ],
  biggestDropoff: { from: "Completed Onboarding", to: "Attended First Class", dropRate: 37 },
  sourceBreakdown: [
    { source: "ORGANIC", count: 124, percentage: 61 },
    { source: "REFERRAL", count: 48, percentage: 24 },
    { source: "GOOGLE_OAUTH", count: 22, percentage: 11 },
    { source: "SOCIAL_SHARE", count: 9, percentage: 4 },
  ],
  activity: { active7d: 89, active30d: 145, inactive7d: 114, inactive30d: 58, avgDaysSinceLastClass: 6.2 },
};

const MOCK_TRAINER_FUNNEL = {
  totalSignups: 44,
  funnel: [
    { step: "Signed Up", count: 44, rate: 100, avgTimeFromPrevious: null },
    { step: "Completed Onboarding", count: 38, rate: 86, avgTimeFromPrevious: null },
    { step: "Went Live (1st)", count: 26, rate: 59, avgTimeFromPrevious: "72.5h" },
    { step: "Went Live (2nd)", count: 19, rate: 43, avgTimeFromPrevious: "96.2h" },
    { step: "Monthly Active", count: 14, rate: 32, avgTimeFromPrevious: null },
  ],
  biggestDropoff: { from: "Completed Onboarding", to: "Went Live (1st)", dropRate: 32 },
  sourceBreakdown: [
    { source: "ORGANIC", count: 28, percentage: 64 },
    { source: "REFERRAL", count: 12, percentage: 27 },
    { source: "GOOGLE_OAUTH", count: 4, percentage: 9 },
  ],
  activity: { active7d: 18, active30d: 31, inactive7d: 26, inactive30d: 13, avgDaysSinceLastLive: 9.8 },
};

const MOCK_SIGNUPS = {
  period: "daily",
  totalSignups: 247,
  timeline: [
    { date: "2026-01-10", total: 3, learners: 2, trainers: 1 },
    { date: "2026-01-11", total: 2, learners: 2, trainers: 0 },
    { date: "2026-01-12", total: 5, learners: 4, trainers: 1 },
    { date: "2026-01-13", total: 4, learners: 3, trainers: 1 },
    { date: "2026-01-14", total: 8, learners: 7, trainers: 1 },
    { date: "2026-01-15", total: 12, learners: 10, trainers: 2 },
    { date: "2026-01-16", total: 6, learners: 5, trainers: 1 },
    { date: "2026-01-17", total: 3, learners: 2, trainers: 1 },
    { date: "2026-01-18", total: 7, learners: 6, trainers: 1 },
    { date: "2026-01-19", total: 15, learners: 13, trainers: 2 },
    { date: "2026-01-20", total: 18, learners: 15, trainers: 3 },
    { date: "2026-01-21", total: 14, learners: 12, trainers: 2 },
    { date: "2026-01-22", total: 9, learners: 8, trainers: 1 },
    { date: "2026-01-23", total: 5, learners: 4, trainers: 1 },
    { date: "2026-01-24", total: 11, learners: 9, trainers: 2 },
    { date: "2026-01-25", total: 8, learners: 7, trainers: 1 },
    { date: "2026-01-26", total: 6, learners: 5, trainers: 1 },
    { date: "2026-01-27", total: 4, learners: 3, trainers: 1 },
    { date: "2026-01-28", total: 13, learners: 11, trainers: 2 },
    { date: "2026-01-29", total: 10, learners: 8, trainers: 2 },
    { date: "2026-01-30", total: 7, learners: 6, trainers: 1 },
    { date: "2026-01-31", total: 9, learners: 7, trainers: 2 },
  ],
  sourceBreakdown: [
    { source: "ORGANIC", count: 152, percentage: 62 },
    { source: "REFERRAL", count: 60, percentage: 24 },
    { source: "GOOGLE_OAUTH", count: 26, percentage: 11 },
    { source: "SOCIAL_SHARE", count: 9, percentage: 3 },
  ],
};

const MOCK_COHORTS = {
  role: "Learner",
  cohorts: [
    { weekLabel: "Dec 1", cohortSize: 14, retention: [{ week: 1, rate: 71 }, { week: 2, rate: 57 }, { week: 3, rate: 43 }, { week: 4, rate: 36 }, { week: 5, rate: 29 }, { week: 6, rate: 21 }, { week: 7, rate: 21 }, { week: 8, rate: 14 }] },
    { weekLabel: "Dec 8", cohortSize: 18, retention: [{ week: 1, rate: 67 }, { week: 2, rate: 50 }, { week: 3, rate: 39 }, { week: 4, rate: 33 }, { week: 5, rate: 28 }, { week: 6, rate: 22 }, { week: 7, rate: 17 }] },
    { weekLabel: "Dec 15", cohortSize: 11, retention: [{ week: 1, rate: 73 }, { week: 2, rate: 55 }, { week: 3, rate: 45 }, { week: 4, rate: 36 }, { week: 5, rate: 27 }, { week: 6, rate: 27 }] },
    { weekLabel: "Dec 22", cohortSize: 8, retention: [{ week: 1, rate: 63 }, { week: 2, rate: 50 }, { week: 3, rate: 38 }, { week: 4, rate: 25 }, { week: 5, rate: 25 }] },
    { weekLabel: "Dec 29", cohortSize: 22, retention: [{ week: 1, rate: 77 }, { week: 2, rate: 59 }, { week: 3, rate: 50 }, { week: 4, rate: 41 }] },
    { weekLabel: "Jan 5", cohortSize: 19, retention: [{ week: 1, rate: 74 }, { week: 2, rate: 58 }, { week: 3, rate: 47 }] },
    { weekLabel: "Jan 12", cohortSize: 25, retention: [{ week: 1, rate: 80 }, { week: 2, rate: 64 }] },
    { weekLabel: "Jan 19", cohortSize: 34, retention: [{ week: 1, rate: 76 }] },
  ],
};

// ============================================
// THEME
// ============================================
const COLORS = {
  bg: "#0B0E11",
  surface: "#141820",
  surfaceHover: "#1A2030",
  border: "#1E2738",
  borderLight: "#2A3548",
  text: "#E8ECF1",
  textSecondary: "#8B95A5",
  textMuted: "#5A6577",
  accent: "#3B82F6",
  accentLight: "#60A5FA",
  green: "#22C55E",
  greenBg: "rgba(34,197,94,0.12)",
  red: "#EF4444",
  redBg: "rgba(239,68,68,0.12)",
  amber: "#F59E0B",
  amberBg: "rgba(245,158,11,0.12)",
  purple: "#A855F7",
  cyan: "#06B6D4",
  chartColors: ["#3B82F6", "#22C55E", "#F59E0B", "#EF4444", "#A855F7", "#06B6D4", "#EC4899"],
  funnelColors: ["#3B82F6", "#60A5FA", "#06B6D4", "#22C55E", "#A855F7"],
};

const sourceColors = {
  ORGANIC: "#3B82F6",
  REFERRAL: "#22C55E",
  GOOGLE_OAUTH: "#F59E0B",
  APPLE_OAUTH: "#8B5CF6",
  SOCIAL_SHARE: "#EC4899",
  STREAM_LINK: "#06B6D4",
  UNKNOWN: "#6B7280",
};

// ============================================
// COMPONENTS
// ============================================

function StatCard({ label, value, subValue, trend, trendLabel, color }) {
  const isPositive = trend > 0;
  const trendColor = isPositive ? COLORS.green : trend < 0 ? COLORS.red : COLORS.textMuted;
  const trendBg = isPositive ? COLORS.greenBg : trend < 0 ? COLORS.redBg : "transparent";

  return (
    <div style={{
      background: COLORS.surface,
      border: `1px solid ${COLORS.border}`,
      borderRadius: 12,
      padding: "20px 24px",
      display: "flex",
      flexDirection: "column",
      gap: 8,
    }}>
      <span style={{ color: COLORS.textSecondary, fontSize: 13, fontWeight: 500, letterSpacing: "0.02em" }}>
        {label}
      </span>
      <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
        <span style={{ color: color || COLORS.text, fontSize: 32, fontWeight: 700, letterSpacing: "-0.02em" }}>
          {typeof value === "number" ? value.toLocaleString() : value}
        </span>
        {trend !== undefined && trend !== null && (
          <span style={{
            color: trendColor,
            background: trendBg,
            fontSize: 12,
            fontWeight: 600,
            padding: "3px 8px",
            borderRadius: 6,
          }}>
            {isPositive ? "↑" : trend < 0 ? "↓" : "→"} {Math.abs(trend)}%
          </span>
        )}
      </div>
      {subValue && (
        <span style={{ color: COLORS.textMuted, fontSize: 12 }}>{subValue}</span>
      )}
    </div>
  );
}

function SectionHeader({ title, subtitle }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <h2 style={{ color: COLORS.text, fontSize: 20, fontWeight: 700, margin: 0, letterSpacing: "-0.01em" }}>
        {title}
      </h2>
      {subtitle && (
        <p style={{ color: COLORS.textSecondary, fontSize: 13, margin: "4px 0 0 0" }}>{subtitle}</p>
      )}
    </div>
  );
}

function FunnelChart({ data, label }) {
  const maxCount = data[0]?.count || 1;

  return (
    <div style={{
      background: COLORS.surface,
      border: `1px solid ${COLORS.border}`,
      borderRadius: 12,
      padding: 24,
    }}>
      <h3 style={{ color: COLORS.text, fontSize: 16, fontWeight: 600, margin: "0 0 20px 0" }}>
        {label} Funnel
      </h3>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {data.map((step, i) => {
          const widthPct = Math.max((step.count / maxCount) * 100, 8);
          const color = COLORS.funnelColors[i % COLORS.funnelColors.length];
          const dropFromPrev = i > 0 ? data[i - 1].count - step.count : 0;
          const dropPct = i > 0 && data[i - 1].count > 0
            ? Math.round((dropFromPrev / data[i - 1].count) * 100)
            : 0;

          return (
            <div key={step.step} style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <div style={{ width: 180, flexShrink: 0 }}>
                <div style={{ color: COLORS.text, fontSize: 13, fontWeight: 500 }}>{step.step}</div>
                {step.avgTimeFromPrevious && (
                  <div style={{ color: COLORS.textMuted, fontSize: 11, marginTop: 2 }}>
                    avg: {step.avgTimeFromPrevious}
                  </div>
                )}
              </div>
              <div style={{ flex: 1, position: "relative" }}>
                <div style={{
                  width: `${widthPct}%`,
                  height: 36,
                  background: `linear-gradient(90deg, ${color}22, ${color}44)`,
                  border: `1px solid ${color}66`,
                  borderRadius: 6,
                  display: "flex",
                  alignItems: "center",
                  paddingLeft: 12,
                  transition: "width 0.6s ease",
                }}>
                  <span style={{ color: COLORS.text, fontSize: 14, fontWeight: 700 }}>
                    {step.count}
                  </span>
                  <span style={{ color: COLORS.textSecondary, fontSize: 12, marginLeft: 6 }}>
                    ({step.rate}%)
                  </span>
                </div>
              </div>
              {i > 0 && dropPct > 0 && (
                <div style={{
                  width: 60,
                  textAlign: "right",
                  color: dropPct > 30 ? COLORS.red : dropPct > 15 ? COLORS.amber : COLORS.textMuted,
                  fontSize: 12,
                  fontWeight: 600,
                  flexShrink: 0,
                }}>
                  −{dropPct}%
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DropoffAlert({ data }) {
  if (!data || !data.from) return null;
  return (
    <div style={{
      background: COLORS.redBg,
      border: `1px solid ${COLORS.red}33`,
      borderRadius: 10,
      padding: "14px 18px",
      display: "flex",
      alignItems: "center",
      gap: 12,
    }}>
      <span style={{ fontSize: 20 }}>⚠️</span>
      <div>
        <div style={{ color: COLORS.red, fontSize: 13, fontWeight: 600 }}>
          Biggest Drop-off: {data.dropRate}%
        </div>
        <div style={{ color: COLORS.textSecondary, fontSize: 12, marginTop: 2 }}>
          Between "{data.from}" → "{data.to}"
        </div>
      </div>
    </div>
  );
}

function ActivityCards({ data, role }) {
  const daysSinceLabel = role === "learner" ? data.avgDaysSinceLastClass : data.avgDaysSinceLastLive;
  const daysSinceKey = role === "learner" ? "last class" : "last live";

  return (
    <div style={{
      background: COLORS.surface,
      border: `1px solid ${COLORS.border}`,
      borderRadius: 12,
      padding: 24,
    }}>
      <h3 style={{ color: COLORS.text, fontSize: 16, fontWeight: 600, margin: "0 0 16px 0" }}>
        {role === "learner" ? "Learner" : "Trainer"} Activity
      </h3>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ color: COLORS.green, fontSize: 28, fontWeight: 700 }}>{data.active7d}</div>
          <div style={{ color: COLORS.textSecondary, fontSize: 12, marginTop: 4 }}>Active 7d</div>
        </div>
        <div style={{ textAlign: "center" }}>
          <div style={{ color: COLORS.accentLight, fontSize: 28, fontWeight: 700 }}>{data.active30d}</div>
          <div style={{ color: COLORS.textSecondary, fontSize: 12, marginTop: 4 }}>Active 30d</div>
        </div>
        <div style={{ textAlign: "center" }}>
          <div style={{ color: COLORS.red, fontSize: 28, fontWeight: 700 }}>{data.inactive30d}</div>
          <div style={{ color: COLORS.textSecondary, fontSize: 12, marginTop: 4 }}>Churned 30d+</div>
        </div>
      </div>
      {daysSinceLabel !== undefined && (
        <div style={{
          marginTop: 16,
          padding: "10px 14px",
          background: COLORS.bg,
          borderRadius: 8,
          textAlign: "center",
        }}>
          <span style={{ color: COLORS.textSecondary, fontSize: 12 }}>
            Avg {daysSinceLabel} days since {daysSinceKey}
          </span>
        </div>
      )}
    </div>
  );
}

function CohortTable({ data }) {
  if (!data || !data.cohorts || data.cohorts.length === 0) {
    return <div style={{ color: COLORS.textMuted, padding: 24 }}>No cohort data available</div>;
  }

  const maxWeeks = Math.max(...data.cohorts.map((c) => c.retention?.length || 0));

  const getCellColor = (rate) => {
    if (rate >= 60) return { bg: "#22C55E33", text: COLORS.green };
    if (rate >= 40) return { bg: "#3B82F633", text: COLORS.accentLight };
    if (rate >= 20) return { bg: "#F59E0B33", text: COLORS.amber };
    return { bg: "#EF444422", text: COLORS.red };
  };

  return (
    <div style={{
      background: COLORS.surface,
      border: `1px solid ${COLORS.border}`,
      borderRadius: 12,
      padding: 24,
      overflowX: "auto",
    }}>
      <h3 style={{ color: COLORS.text, fontSize: 16, fontWeight: 600, margin: "0 0 16px 0" }}>
        {data.role} Cohort Retention
      </h3>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr>
            <th style={{ textAlign: "left", padding: "8px 12px", color: COLORS.textSecondary, fontWeight: 500, borderBottom: `1px solid ${COLORS.border}` }}>
              Cohort
            </th>
            <th style={{ textAlign: "center", padding: "8px 12px", color: COLORS.textSecondary, fontWeight: 500, borderBottom: `1px solid ${COLORS.border}` }}>
              Size
            </th>
            {Array.from({ length: maxWeeks }, (_, i) => (
              <th key={i} style={{ textAlign: "center", padding: "8px 12px", color: COLORS.textSecondary, fontWeight: 500, borderBottom: `1px solid ${COLORS.border}` }}>
                Wk {i + 1}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.cohorts.map((cohort, ci) => (
            <tr key={ci}>
              <td style={{ padding: "8px 12px", color: COLORS.text, fontWeight: 500, borderBottom: `1px solid ${COLORS.border}` }}>
                {cohort.weekLabel}
              </td>
              <td style={{ textAlign: "center", padding: "8px 12px", color: COLORS.textSecondary, borderBottom: `1px solid ${COLORS.border}` }}>
                {cohort.cohortSize}
              </td>
              {Array.from({ length: maxWeeks }, (_, wi) => {
                const ret = cohort.retention?.[wi];
                if (!ret) {
                  return (
                    <td key={wi} style={{ textAlign: "center", padding: "8px 12px", borderBottom: `1px solid ${COLORS.border}` }}>
                      <span style={{ color: COLORS.textMuted }}>—</span>
                    </td>
                  );
                }
                const colors = getCellColor(ret.rate);
                return (
                  <td key={wi} style={{ textAlign: "center", padding: "6px 8px", borderBottom: `1px solid ${COLORS.border}` }}>
                    <span style={{
                      background: colors.bg,
                      color: colors.text,
                      padding: "4px 10px",
                      borderRadius: 6,
                      fontWeight: 600,
                      fontSize: 12,
                    }}>
                      {ret.rate}%
                    </span>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ display: "flex", gap: 16, marginTop: 16, justifyContent: "center" }}>
        {[
          { label: "60%+", color: "#22C55E33", text: COLORS.green },
          { label: "40-59%", color: "#3B82F633", text: COLORS.accentLight },
          { label: "20-39%", color: "#F59E0B33", text: COLORS.amber },
          { label: "<20%", color: "#EF444422", text: COLORS.red },
        ].map((l) => (
          <div key={l.label} style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ width: 14, height: 14, borderRadius: 4, background: l.color, border: `1px solid ${l.text}44` }} />
            <span style={{ color: COLORS.textMuted, fontSize: 11 }}>{l.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SourcePieChart({ data }) {
  if (!data || data.length === 0) return null;

  return (
    <div style={{
      background: COLORS.surface,
      border: `1px solid ${COLORS.border}`,
      borderRadius: 12,
      padding: 24,
    }}>
      <h3 style={{ color: COLORS.text, fontSize: 16, fontWeight: 600, margin: "0 0 8px 0" }}>
        Signup Sources
      </h3>
      <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
        <ResponsiveContainer width="50%" height={180}>
          <PieChart>
            <Pie
              data={data}
              dataKey="count"
              nameKey="source"
              cx="50%"
              cy="50%"
              innerRadius={45}
              outerRadius={75}
              strokeWidth={0}
            >
              {data.map((entry, i) => (
                <Cell key={i} fill={sourceColors[entry.source] || COLORS.textMuted} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {data.map((entry, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{
                width: 10,
                height: 10,
                borderRadius: 3,
                background: sourceColors[entry.source] || COLORS.textMuted,
              }} />
              <span style={{ color: COLORS.textSecondary, fontSize: 12, width: 100 }}>
                {entry.source.replace(/_/g, " ")}
              </span>
              <span style={{ color: COLORS.text, fontSize: 13, fontWeight: 600 }}>
                {entry.count}
              </span>
              <span style={{ color: COLORS.textMuted, fontSize: 11 }}>
                ({entry.percentage}%)
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const customTooltipStyle = {
  background: COLORS.surface,
  border: `1px solid ${COLORS.borderLight}`,
  borderRadius: 8,
  padding: "10px 14px",
  boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
};

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload) return null;
  return (
    <div style={customTooltipStyle}>
      <div style={{ color: COLORS.textSecondary, fontSize: 12, marginBottom: 6 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 2 }}>
          <div style={{ width: 8, height: 8, borderRadius: 2, background: p.color }} />
          <span style={{ color: COLORS.textSecondary, fontSize: 12 }}>{p.name}:</span>
          <span style={{ color: COLORS.text, fontSize: 12, fontWeight: 600 }}>{p.value}</span>
        </div>
      ))}
    </div>
  );
}

// ============================================
// TABS
// ============================================
const TABS = [
  { id: "overview", label: "Overview" },
  { id: "learners", label: "Learner Funnel" },
  { id: "trainers", label: "Trainer Funnel" },
  { id: "signups", label: "Signup Timeline" },
  { id: "cohorts", label: "Cohort Analysis" },
];

// ============================================
// MAIN DASHBOARD
// ============================================
export default function AnalyticsDashboard() {
  const [activeTab, setActiveTab] = useState("overview");
  const [useMock, setUseMock] = useState(false);
  
  const [token, setToken] = useState("");
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginError, setLoginError] = useState("");

  // Data states
  const [overview, setOverview] = useState(MOCK_OVERVIEW);
  const [learnerFunnel, setLearnerFunnel] = useState(MOCK_LEARNER_FUNNEL);
  const [trainerFunnel, setTrainerFunnel] = useState(MOCK_TRAINER_FUNNEL);
  const [signups, setSignups] = useState(MOCK_SIGNUPS);
  const [learnerCohorts, setLearnerCohorts] = useState(MOCK_COHORTS);
  const [trainerCohorts, setTrainerCohorts] = useState({ ...MOCK_COHORTS, role: "Trainer" });
  const [cohortRole, setCohortRole] = useState("Learner");

  // Period controls for signups
  const [signupPeriod, setSignupPeriod] = useState("daily");

  useEffect(() => {
      if (useMock || !token) return;
      const headers = { Authorization: `Bearer ${token}` };
    
    Promise.all([
      fetch(`${API_BASE}/api/analytics/overview`, { headers }).then(r => r.json()),
      fetch(`${API_BASE}/api/analytics/funnel/learners`, { headers }).then(r => r.json()),
      fetch(`${API_BASE}/api/analytics/funnel/trainers`, { headers }).then(r => r.json()),
      fetch(`${API_BASE}/api/analytics/signups?period=${signupPeriod}`, { headers }).then(r => r.json()),
      fetch(`${API_BASE}/api/analytics/cohorts/learners?weeksBack=12`, { headers }).then(r => r.json()),
      fetch(`${API_BASE}/api/analytics/cohorts/trainers?weeksBack=12`, { headers }).then(r => r.json()),
    ]).then(([ov, lf, tf, sg, lc, tc]) => {
      setOverview(ov);
      setLearnerFunnel(lf);
      setTrainerFunnel(tf);
      setSignups(sg);
      setLearnerCohorts(lc);
      setTrainerCohorts(tc);
    }).catch(err => console.error("Failed to fetch analytics:", err));
  }, [useMock, signupPeriod, token]);

const handleLogin = async () => {
    try {
      setLoginError("");
      const res = await fetch(`${API_BASE}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ emailOrUsername: loginEmail, password: loginPassword }),
      });
      const data = await res.json();
      if (data.access_token) {
        setToken(data.access_token);
      } else {
        setLoginError("Invalid credentials");
      }
    } catch (err) {
      setLoginError("Connection failed");
    }
  };


  return (
    <div style={{
      minHeight: "100vh",
      background: COLORS.bg,
      color: COLORS.text,
      fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
    }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />

    {!token && !useMock && (
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "center",
          minHeight: "100vh", flexDirection: "column", gap: 16,
        }}>
          <h2 style={{ color: COLORS.text, fontSize: 24, fontWeight: 700 }}>Fitneks Admin Login</h2>
          <input
            type="email" placeholder="Email" value={loginEmail}
            onChange={(e) => setLoginEmail(e.target.value)}
            style={{ padding: "10px 14px", borderRadius: 8, border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.text, width: 280, fontSize: 14 }}
          />
          <input
            type="password" placeholder="Password" value={loginPassword}
            onChange={(e) => setLoginPassword(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleLogin()}
            style={{ padding: "10px 14px", borderRadius: 8, border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.text, width: 280, fontSize: 14 }}
          />
          <button onClick={handleLogin} style={{
            padding: "10px 24px", borderRadius: 8, border: "none", cursor: "pointer",
            background: COLORS.accent, color: "#fff", fontWeight: 600, fontSize: 14,
          }}>
            Log In
          </button>
          {loginError && <span style={{ color: COLORS.red, fontSize: 13 }}>{loginError}</span>}
        </div>
      )}


    {(token || useMock) && <>{/* Header */}
      <header style={{
        borderBottom: `1px solid ${COLORS.border}`,
        padding: "16px 32px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        position: "sticky",
        top: 0,
        background: COLORS.bg,
        zIndex: 10,
        backdropFilter: "blur(12px)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            background: `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.purple})`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 700,
            fontSize: 14,
            color: "#fff",
          }}>
            F
          </div>
          <span style={{ fontWeight: 700, fontSize: 18, letterSpacing: "-0.01em" }}>
            Fitneks Analytics
          </span>
          <span style={{
            background: COLORS.greenBg,
            color: COLORS.green,
            fontSize: 11,
            fontWeight: 600,
            padding: "3px 8px",
            borderRadius: 6,
            marginLeft: 4,
          }}>
            ADMIN
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={useMock}
              onChange={(e) => setUseMock(e.target.checked)}
              style={{ accentColor: COLORS.accent }}
            />
            <span style={{ color: COLORS.textSecondary, fontSize: 12 }}>Demo Data</span>
          </label>
        </div>
      </header>

      {/* Tabs */}
      <nav style={{
        display: "flex",
        gap: 4,
        padding: "12px 32px",
        borderBottom: `1px solid ${COLORS.border}`,
        overflowX: "auto",
      }}>
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: "8px 16px",
              borderRadius: 8,
              border: "none",
              cursor: "pointer",
              fontSize: 13,
              fontWeight: 600,
              fontFamily: "inherit",
              transition: "all 0.15s ease",
              background: activeTab === tab.id ? COLORS.accent + "22" : "transparent",
              color: activeTab === tab.id ? COLORS.accentLight : COLORS.textSecondary,
              borderBottom: activeTab === tab.id ? `2px solid ${COLORS.accent}` : "2px solid transparent",
            }}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {/* Content */}
      <main style={{ padding: "24px 32px", maxWidth: 1200, margin: "0 auto" }}>

        {/* =========== OVERVIEW TAB =========== */}
        {activeTab === "overview" && (
          <div>
            <SectionHeader title="Dashboard Overview" subtitle="Key metrics at a glance" />
            
            {/* Top Stats */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 24 }}>
              <StatCard label="Total Users" value={overview.totalUsers} subValue={`${overview.totalLearners} learners · ${overview.totalTrainers} trainers`} />
              <StatCard label="Signups This Week" value={overview.signupsThisWeek} trend={overview.signupTrend} subValue={`vs ${overview.signupsLastWeek} last week`} />
              <StatCard label="Learner Retention (30d)" value={`${overview.learnerRetention30d}%`} color={overview.learnerRetention30d >= 50 ? COLORS.green : COLORS.amber} subValue={`${overview.activeLearners30d} active of ${overview.totalLearners}`} />
              <StatCard label="Trainer Retention (30d)" value={`${overview.trainerRetention30d}%`} color={overview.trainerRetention30d >= 50 ? COLORS.green : COLORS.amber} subValue={`${overview.activeTrainers30d} active of ${overview.totalTrainers}`} />
            </div>

            {/* Second row */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 24 }}>
              <StatCard label="Active Learners (7d)" value={overview.activeLearners7d} color={COLORS.green} />
              <StatCard label="Active Trainers (7d)" value={overview.activeTrainers7d} color={COLORS.green} />
              <StatCard label="Streams Completed" value={overview.totalStreamsCompleted} color={COLORS.cyan} />
              <StatCard label="Active Learners (30d)" value={overview.activeLearners30d} color={COLORS.accentLight} />
            </div>

            {/* Quick Signup Chart */}
            <div style={{
              background: COLORS.surface,
              border: `1px solid ${COLORS.border}`,
              borderRadius: 12,
              padding: 24,
            }}>
              <h3 style={{ color: COLORS.text, fontSize: 16, fontWeight: 600, margin: "0 0 16px 0" }}>
                Recent Signups (Daily)
              </h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={signups.timeline.slice(-14)} barGap={2}>
                  <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(d) => d.slice(5)}
                    stroke={COLORS.textMuted}
                    fontSize={11}
                    tick={{ fill: COLORS.textMuted }}
                  />
                  <YAxis stroke={COLORS.textMuted} fontSize={11} tick={{ fill: COLORS.textMuted }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="learners" name="Learners" fill={COLORS.accent} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="trainers" name="Trainers" fill={COLORS.purple} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* =========== LEARNER FUNNEL TAB =========== */}
        {activeTab === "learners" && (
          <div>
            <SectionHeader title="Learner Funnel" subtitle="Signup → First Class → Second Class → Monthly Active" />
            <DropoffAlert data={learnerFunnel.biggestDropoff} />
            <div style={{ marginTop: 16 }}>
              <FunnelChart data={learnerFunnel.funnel} label="Learner" />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 16 }}>
              <ActivityCards data={learnerFunnel.activity} role="learner" />
              <SourcePieChart data={learnerFunnel.sourceBreakdown} />
            </div>
          </div>
        )}

        {/* =========== TRAINER FUNNEL TAB =========== */}
        {activeTab === "trainers" && (
          <div>
            <SectionHeader title="Trainer Funnel" subtitle="Signup → Profile Complete → First Live → Second Live → Monthly Active" />
            <DropoffAlert data={trainerFunnel.biggestDropoff} />
            <div style={{ marginTop: 16 }}>
              <FunnelChart data={trainerFunnel.funnel} label="Trainer" />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 16 }}>
              <ActivityCards data={trainerFunnel.activity} role="trainer" />
              <SourcePieChart data={trainerFunnel.sourceBreakdown} />
            </div>
          </div>
        )}

        {/* =========== SIGNUP TIMELINE TAB =========== */}
        {activeTab === "signups" && (
          <div>
            <SectionHeader title="Signup Timeline" subtitle="Track signups over time to measure campaign impact" />
            
            {/* Period selector */}
            <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
              {["daily", "weekly", "monthly"].map((p) => (
                <button
                  key={p}
                  onClick={() => setSignupPeriod(p)}
                  style={{
                    padding: "6px 14px",
                    borderRadius: 6,
                    border: `1px solid ${signupPeriod === p ? COLORS.accent : COLORS.border}`,
                    background: signupPeriod === p ? COLORS.accent + "22" : "transparent",
                    color: signupPeriod === p ? COLORS.accentLight : COLORS.textSecondary,
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer",
                    fontFamily: "inherit",
                  }}
                >
                  {p.charAt(0).toUpperCase() + p.slice(1)}
                </button>
              ))}
            </div>

            {/* Line Chart */}
            <div style={{
              background: COLORS.surface,
              border: `1px solid ${COLORS.border}`,
              borderRadius: 12,
              padding: 24,
              marginBottom: 16,
            }}>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={signups.timeline}>
                  <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(d) => d.slice(5)}
                    stroke={COLORS.textMuted}
                    fontSize={11}
                    tick={{ fill: COLORS.textMuted }}
                  />
                  <YAxis stroke={COLORS.textMuted} fontSize={11} tick={{ fill: COLORS.textMuted }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Line type="monotone" dataKey="total" name="Total" stroke={COLORS.text} strokeWidth={2} dot={{ r: 3, fill: COLORS.text }} />
                  <Line type="monotone" dataKey="learners" name="Learners" stroke={COLORS.accent} strokeWidth={2} dot={{ r: 3, fill: COLORS.accent }} />
                  <Line type="monotone" dataKey="trainers" name="Trainers" stroke={COLORS.purple} strokeWidth={2} dot={{ r: 3, fill: COLORS.purple }} />
                  <Legend
                    verticalAlign="top"
                    height={36}
                    formatter={(value) => <span style={{ color: COLORS.textSecondary, fontSize: 12 }}>{value}</span>}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Source breakdown */}
            <SourcePieChart data={signups.sourceBreakdown} />

            {/* Campaign annotation hint */}
            <div style={{
              marginTop: 16,
              background: COLORS.surface,
              border: `1px solid ${COLORS.border}`,
              borderRadius: 10,
              padding: "14px 18px",
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}>
              <span style={{ fontSize: 18 }}>💡</span>
              <span style={{ color: COLORS.textSecondary, fontSize: 13 }}>
                Match signup spikes to your campaign dates. A spike on Jan 19-20 with 15-18 signups/day
                vs the baseline of 3-6/day tells you that campaign drove ~3x normal signups.
              </span>
            </div>
          </div>
        )}

        {/* =========== COHORT ANALYSIS TAB =========== */}
        {activeTab === "cohorts" && (
          <div>
            <SectionHeader title="Cohort Retention" subtitle="Are newer users retaining better than older ones?" />
            
            {/* Role toggle */}
            <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
              {["Learner", "Trainer"].map((r) => (
                <button
                  key={r}
                  onClick={() => setCohortRole(r)}
                  style={{
                    padding: "6px 14px",
                    borderRadius: 6,
                    border: `1px solid ${cohortRole === r ? COLORS.accent : COLORS.border}`,
                    background: cohortRole === r ? COLORS.accent + "22" : "transparent",
                    color: cohortRole === r ? COLORS.accentLight : COLORS.textSecondary,
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer",
                    fontFamily: "inherit",
                  }}
                >
                  {r}s
                </button>
              ))}
            </div>

            <CohortTable data={cohortRole === "Learner" ? learnerCohorts : trainerCohorts} />

            {/* Reading guide */}
            <div style={{
              marginTop: 16,
              background: COLORS.surface,
              border: `1px solid ${COLORS.border}`,
              borderRadius: 10,
              padding: "14px 18px",
              display: "flex",
              alignItems: "flex-start",
              gap: 12,
            }}>
              <span style={{ fontSize: 18, marginTop: 2 }}>📖</span>
              <div style={{ color: COLORS.textSecondary, fontSize: 13, lineHeight: 1.5 }}>
                <strong style={{ color: COLORS.text }}>How to read this:</strong> Each row is a group of users who signed up the same week.
                Columns show what percentage came back in subsequent weeks.
                If Week 1 retention is improving for newer cohorts, your onboarding is getting better.
                If all cohorts drop below 20% by Week 4, you have a retention problem.
              </div>
            </div>
          </div>
        )}
      </main>
      </>}
    </div>
  );
}